/**
 * The results page's marks on one frame of the analysed clip (OverlayPlayer.jsx; backend _draw_page_marks), as
 * shapes in the pixels of a picture `width` wide: a 1 s trail per animal (older parts fainter, cut at jumps over half a
 * body length), a see-through dot in its kind's colour, a light-green ring on what a person added, a pulse for an
 * animal counted in the last frames, and the dashed counting line. Sizes follow the width, as on paper. An animal is
 * drawn from the frame it is counted (the index holds no earlier points), and its trail stops there too.
 */

import { shownFrom } from '../components/overlay/tracksModel';

const GREEN = '32, 172, 107';
const TRAIL_SECONDS = 1;
const TRAIL_BANDS = 4;
const PULSE_FRAMES = 8;
// of the picture width: a dot about 1.2 mm across on the page
const DOT_RADIUS = 0.006;
const MARK_WIDTH = 0.003;
const DASH = [0.016, 0.011];

/** { radius, lineWidth, trails: [{ rgb, band, alpha, segments }], dots, pulses, line } for `frame` of `index`. */
export const marksAt = (index, frame, { width, kindColors = {}, line = null }) => {
	const s = width / index.width;
	const radius = Math.max(3, Math.round(DOT_RADIUS * width));
	const trail = Math.max(2, Math.round(TRAIL_SECONDS * index.fps));
	const f = Math.min(index.nFrames - 1, Math.max(0, frame));
	const rgbOf = tr => kindColors[tr.label]?.rgb || GREEN;
	const visible = [];
	for (let i = index.perFrame[f]; i < index.perFrame[f + 1]; i += 1)
		visible.push([index.tracks[index.trackOf[i]], index.pointOf[i]]);

	const bands = new Map();
	visible.forEach(([tr, j]) => {
		const body = 0.5 * Math.sqrt(Math.max(1, tr.w[j] * tr.h[j]));
		const from = shownFrom(tr);
		for (let k = j; k > 0; k -= 1) {
			const age = f - tr.t[k - 1];
			if (
				age > trail ||
				tr.t[k - 1] < from ||
				Math.hypot(tr.x[k] - tr.x[k - 1], tr.y[k] - tr.y[k - 1]) > body
			)
				break;
			const band = Math.min(TRAIL_BANDS - 1, Math.floor((age / trail) * TRAIL_BANDS));
			const key = `${rgbOf(tr)}|${band}`;
			if (!bands.has(key))
				bands.set(key, {
					rgb: rgbOf(tr),
					band,
					alpha: 0.8 * (1 - band / TRAIL_BANDS),
					segments: [],
				});
			bands
				.get(key)
				.segments.push([tr.x[k] * s, tr.y[k] * s, tr.x[k - 1] * s, tr.y[k - 1] * s]);
		}
	});

	return {
		radius,
		lineWidth: Math.max(2, Math.round(MARK_WIDTH * width)),
		// the oldest band first, so the newer parts lie on top
		trails: [...bands.values()].sort((a, b) => b.band - a.band),
		dots: visible.map(([tr, j]) => ({
			x: tr.x[j] * s,
			y: tr.y[j] * s,
			rgb: rgbOf(tr),
			added: !!tr.added,
		})),
		pulses: visible
			.map(([tr, j]) => ({ tr, j, age: f - (tr.confirmed_at ?? tr.t0) }))
			.filter(({ age }) => age >= 0 && age < PULSE_FRAMES)
			.map(({ tr, j, age }) => ({
				x: tr.x[j] * s,
				y: tr.y[j] * s,
				r: radius * (1.8 + (4 * age) / PULSE_FRAMES),
				alpha: 0.8 * (1 - age / PULSE_FRAMES),
			})),
		line: line
			? {
					x1: line.start[0] * s,
					y1: line.start[1] * s,
					x2: line.end[0] * s,
					y2: line.end[1] * s,
					dash: DASH.map(d => d * width),
					width: Math.max(2, Math.round(1.2 * MARK_WIDTH * width)),
				}
			: null,
	};
};
